<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex min-w-0 items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Saved Flyers</p>
              <h1 class="mt-1 text-2xl font-semibold text-slate-950 sm:text-3xl">Saved Collection</h1>
              <p class="mt-1 text-sm text-slate-500">Revisit flyers you bookmarked from Explore.</p>
            </div>
          </div>

          <div class="inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
            <i class="ri-bookmark-fill" aria-hidden="true"></i>
            <span>{{ savedFlyers.length }} saved</span>
          </div>
        </div>
      </div>

      <div class="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <p class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Archive</p>
            <h2 class="mt-1 text-xl font-semibold text-slate-950">All saved flyers</h2>
          </div>
          <span class="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500">
            {{ savedFlyers.length }} total
          </span>
        </div>

        <div v-if="isLoading" class="saved-flyers-grid mt-5">
          <div
            v-for="(column, columnIndex) in skeletonFlyerColumns"
            :key="`saved-skeleton-column-${columnIndex}`"
            class="saved-flyers-column"
          >
            <FlyerCardSkeleton v-for="index in column" :key="`saved-skeleton-${index}`" :index="index" />
          </div>
        </div>

        <div v-else-if="!savedFlyers.length" class="py-12 text-center">
          <p class="m-0 text-lg font-bold text-slate-900">No saved flyers yet</p>
          <p class="mt-2 text-sm font-medium text-slate-500">
            Save flyers from Explore and they will appear here.
          </p>
        </div>

        <div v-else class="saved-flyers-grid mt-5">
          <div
            v-for="(column, columnIndex) in savedFlyerColumns"
            :key="`saved-column-${columnIndex}`"
            class="saved-flyers-column"
          >
            <FlyerCard v-for="flyer in column" :key="flyer.id" :flyer="flyer" view="masonry" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import api from '@/services/api'
import { formatFlyerListDate, parseApiDate } from '@/utils/date'
import { useSessionStore } from '@/stores/session'

const session = useSessionStore()
const isLoading = ref(false)
const savedFlyers = ref([])
const masonryColumnCount = ref(1)

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => {
    columns[index % count].push(item)
  })
  return columns
}

const savedFlyerColumns = computed(() => distributeByColumn(savedFlyers.value, masonryColumnCount.value))
const skeletonFlyerColumns = computed(() => distributeByColumn(Array.from({ length: 8 }, (_, index) => index + 1), masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  masonryColumnCount.value = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 2 : 1
}

const buildImageUrl = (value) => {
  const image = Array.isArray(value) ? value[0] : value
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return image
  return `https://test.amplifierapp.xyz/${String(image).replace(/^\/+/, '')}`
}

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === '1' || normalized === 'active' || normalized === 'published' || normalized === 'true') {
    return 'Active'
  }
  if (normalized === '0' || normalized === 'inactive' || normalized === 'draft' || normalized === 'false') {
    return 'Inactive'
  }
  return 'Inactive'
}

const resolveFlyerDate = (item = {}) => {
  const candidates = [
    item.posted_at,
    item.post_date,
    item.created_at,
    item.created_time,
    item.created_date,
    item.createdDate,
    item.created_on,
    item.saved_at,
    item.saved_date,
    item.saved_on,
    item.save_date,
    item.last_created_date,
    item.updated_at,
    item.updated_date,
    item.datetime,
    item.date_time,
    item.date,
    item.groupDate
  ]

  return candidates.find((value) => parseApiDate(value)) || ''
}

const normalizeFlyer = (item = {}, index = 0) => {
  const dateValue = resolveFlyerDate(item)
  const parsedDate = parseApiDate(dateValue)
  const fallbackImage = Array.isArray(item.images) ? item.images[0] : ''
  const city = item.city || item.area || item.locality || ''
  const locationText = item.location || item.location_name || item.address || city || item.state || item.street || item.country_id || item.country || ''
  const rawCategory = item.category || item.content_type || item.category_id || ''
  const categoryName =
    item.category_name ||
    item.catergory_name ||
    item.cat_name ||
    item.content_type_name ||
    item.flyer_category ||
    item.category_title ||
    (rawCategory && Number.isNaN(Number(rawCategory)) ? rawCategory : '') ||
    'Flyer'

  return {
    id: item.id || item.flyer_id || `saved-${index}`,
    title: item.title || item.name || item.flyer_title || '',
    category: categoryName,
    category_name: categoryName,
    contentTypeId: item.content_type || item.content_type_id || item.category_id || '',
    description: item.description || item.desc || item.caption || '',
    image: buildImageUrl(item.image || item.flyer_image || item.image_url || item.image1 || fallbackImage || ''),
    param_code: item.param_code || item.paramCode || item.code || item.color_code || item.colorCode || item.color || '',
    code: item.code || item.param_code || item.paramCode || item.color_code || item.colorCode || item.color || '',
    accent: item.accent || item.categoryColor || '#64748b',
    address: item.address || item.flyer_address || item.location_name || item.location || city || '',
    city,
    area: item.area || '',
    locality: item.locality || '',
    state: item.state || '',
    street: item.street || '',
    country_id: item.country_id || '',
    country: item.country || '',
    location: locationText,
    status: normalizeStatus(item.status ?? item.publish_status),
    userId: item.user_id || item.userid || item.uid || '',
    author: item.author || item.user_name || item.username || 'NetFlyr',
    authorAvatar: buildImageUrl(item.authorAvatar || item.user_img || item.profile_pic || item.user_image || item.profile_image || ''),
    likesCount: item.likesCount || item.likes_count || item.likes || 0,
    viewsCount: item.viewsCount || item.views_count || item.views || 0,
    hashTags: item.hashTags || item.hash_tags || '',
    date: formatFlyerListDate(parsedDate || dateValue) || '',
    postedAt: dateValue,
    saved: true
  }
}

const flattenPayloadRows = (payload) => {
  const flattenGroupedRows = (groups) =>
    Object.entries(groups || {}).flatMap(([dateKey, items = []]) =>
      Array.isArray(items)
        ? items.map((item) => ({
            ...item,
            created_date: item.created_date || item.date || dateKey,
            groupDate: dateKey
          }))
        : []
    )

  if (Array.isArray(payload?.data)) return payload.data
  if (payload?.data && typeof payload.data === 'object') return flattenGroupedRows(payload.data)
  if (Array.isArray(payload?.list)) return payload.list
  if (payload?.list && typeof payload.list === 'object') {
    return flattenGroupedRows(payload.list)
  }
  if (Array.isArray(payload?.saved_list)) return payload.saved_list
  if (payload?.saved_list && typeof payload.saved_list === 'object') return flattenGroupedRows(payload.saved_list)
  return []
}

const loadSavedFlyers = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    savedFlyers.value = []
    return
  }

  isLoading.value = true

  try {
    const { data } = await api.savedFlyersUser({
      page: 1,
      user_id: userId
    })

    const rows = flattenPayloadRows(data)
    savedFlyers.value = rows.map(normalizeFlyer)
  } catch {
    savedFlyers.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  updateMasonryColumnCount()
  window.addEventListener('resize', updateMasonryColumnCount)
  loadSavedFlyers()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMasonryColumnCount)
})
</script>

<style scoped>
.saved-flyers-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.saved-flyers-column {
  display: grid;
  min-width: 0;
  gap: 1rem;
  align-content: start;
}

.saved-flyers-column :deep(.flyer-card-skeleton) {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .saved-flyers-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .saved-flyers-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
